/**
 * Prompt Diff Utility
 *
 * Generates and renders unified diffs between two prompt strings
 * with git-style coloring using chalk.
 */

import chalk from 'chalk';

// =============================================================================
// Types
// =============================================================================

export interface DiffLine {
  type: 'add' | 'remove' | 'context' | 'hunk-header';
  content: string;
  oldLineNo?: number;
  newLineNo?: number;
}

interface RenderOptions {
  maxLines?: number;
  noColor?: boolean;
}

// =============================================================================
// LCS-based Diff Algorithm
// =============================================================================

/**
 * Compute the longest common subsequence table for two string arrays.
 * Returns a 2D array where lcs[i][j] = length of LCS of a[0..i-1] and b[0..j-1].
 */
function computeLcsTable(a: readonly string[], b: readonly string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const table: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );

  for (let i = 1; i <= m; i++) {
    const row = table[i];
    const prevRow = table[i - 1];
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        const prev = prevRow?.[j - 1] ?? 0;
        if (row) row[j] = prev + 1;
      } else {
        const fromAbove = prevRow?.[j] ?? 0;
        const fromLeft = row?.[j - 1] ?? 0;
        if (row) row[j] = Math.max(fromAbove, fromLeft);
      }
    }
  }

  return table;
}

interface RawDiffLine {
  type: 'add' | 'remove' | 'equal';
  content: string;
  oldIdx?: number;
  newIdx?: number;
}

/**
 * Backtrack through the LCS table to produce a sequence of raw diff operations.
 */
function backtrackDiff(
  table: number[][],
  a: readonly string[],
  b: readonly string[],
): RawDiffLine[] {
  const result: RawDiffLine[] = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      result.push({
        type: 'equal',
        content: a[i - 1] ?? '',
        oldIdx: i - 1,
        newIdx: j - 1,
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || (table[i]?.[j - 1] ?? 0) >= (table[i - 1]?.[j] ?? 0))) {
      result.push({ type: 'add', content: b[j - 1] ?? '', newIdx: j - 1 });
      j--;
    } else {
      result.push({ type: 'remove', content: a[i - 1] ?? '', oldIdx: i - 1 });
      i--;
    }
  }

  return result.reverse();
}

// =============================================================================
// Hunk Generation
// =============================================================================

interface Hunk {
  oldStart: number;
  oldCount: number;
  newStart: number;
  newCount: number;
  lines: DiffLine[];
}

/**
 * Group raw diff lines into unified-diff hunks with surrounding context.
 */
function buildHunks(rawLines: RawDiffLine[], contextLines: number): Hunk[] {
  // Find indices of changed lines
  const changeIndices: number[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    const raw = rawLines[i];
    if (raw && raw.type !== 'equal') {
      changeIndices.push(i);
    }
  }

  if (changeIndices.length === 0) return [];

  // Group changes that are within (2 * contextLines) of each other
  const firstIdx = changeIndices[0] ?? 0;
  const groups: { start: number; end: number }[] = [];
  let groupStart = firstIdx;
  let groupEnd = firstIdx;

  for (let i = 1; i < changeIndices.length; i++) {
    const idx = changeIndices[i] ?? 0;
    if (idx - groupEnd <= contextLines * 2) {
      groupEnd = idx;
    } else {
      groups.push({ start: groupStart, end: groupEnd });
      groupStart = idx;
      groupEnd = idx;
    }
  }
  groups.push({ start: groupStart, end: groupEnd });

  // Build hunks from groups
  const hunks: Hunk[] = [];

  for (const group of groups) {
    const rangeStart = Math.max(0, group.start - contextLines);
    const rangeEnd = Math.min(rawLines.length - 1, group.end + contextLines);

    const lines: DiffLine[] = [];
    let oldLine = 0;
    let newLine = 0;

    // Calculate starting line numbers by scanning up to rangeStart
    for (let i = 0; i < rangeStart; i++) {
      const raw = rawLines[i];
      if (raw && (raw.type === 'equal' || raw.type === 'remove')) oldLine++;
      if (raw && (raw.type === 'equal' || raw.type === 'add')) newLine++;
    }

    const oldStart = oldLine + 1;
    const newStart = newLine + 1;
    let oldCount = 0;
    let newCount = 0;

    for (let i = rangeStart; i <= rangeEnd; i++) {
      const raw = rawLines[i];
      if (!raw) continue;
      if (raw.type === 'equal') {
        oldLine++;
        newLine++;
        oldCount++;
        newCount++;
        lines.push({
          type: 'context',
          content: raw.content,
          oldLineNo: oldLine,
          newLineNo: newLine,
        });
      } else if (raw.type === 'remove') {
        oldLine++;
        oldCount++;
        lines.push({
          type: 'remove',
          content: raw.content,
          oldLineNo: oldLine,
        });
      } else {
        newLine++;
        newCount++;
        lines.push({
          type: 'add',
          content: raw.content,
          newLineNo: newLine,
        });
      }
    }

    // Insert hunk header at beginning
    const header = `@@ -${String(oldStart)},${String(oldCount)} +${String(newStart)},${String(newCount)} @@`;
    lines.unshift({ type: 'hunk-header', content: header });

    hunks.push({ oldStart, oldCount, newStart, newCount, lines });
  }

  return hunks;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Generate a unified diff between two prompt strings.
 *
 * @param original - The original prompt text
 * @param mutated - The mutated/optimized prompt text
 * @param contextLines - Number of context lines around changes (default: 3)
 * @returns Array of DiffLine objects representing the unified diff
 */
export function generatePromptDiff(
  original: string,
  mutated: string,
  contextLines = 3,
): DiffLine[] {
  if (original === mutated) return [];

  const oldLines = original.split('\n');
  const newLines = mutated.split('\n');

  const lcsTable = computeLcsTable(oldLines, newLines);
  const rawDiff = backtrackDiff(lcsTable, oldLines, newLines);
  const hunks = buildHunks(rawDiff, contextLines);

  const result: DiffLine[] = [];
  for (const hunk of hunks) {
    result.push(...hunk.lines);
  }

  return result;
}

/**
 * Render diff lines to a displayable string with optional chalk coloring.
 *
 * @param lines - Array of DiffLine objects from generatePromptDiff
 * @param options - Rendering options (maxLines, noColor)
 * @returns Formatted string ready for console output
 */
export function renderDiffLines(
  lines: DiffLine[],
  options?: RenderOptions,
): string {
  const maxLines = options?.maxLines ?? 40;
  const noColor = options?.noColor ?? false;

  if (lines.length === 0) return '';

  const outputLines: string[] = [];
  let linesRendered = 0;
  let truncated = false;

  for (const line of lines) {
    if (linesRendered >= maxLines) {
      truncated = true;
      break;
    }

    let text: string;
    switch (line.type) {
      case 'hunk-header':
        text = noColor ? line.content : chalk.cyan(line.content);
        break;
      case 'add':
        text = noColor ? `+${line.content}` : chalk.green(`+${line.content}`);
        break;
      case 'remove':
        text = noColor ? `-${line.content}` : chalk.red(`-${line.content}`);
        break;
      case 'context':
        text = noColor ? ` ${line.content}` : chalk.dim(` ${line.content}`);
        break;
    }

    outputLines.push(text);
    linesRendered++;
  }

  if (truncated) {
    const remaining = lines.length - maxLines;
    const summary = `... ${String(remaining)} more lines changed`;
    outputLines.push(noColor ? summary : chalk.yellow(summary));
  }

  return outputLines.join('\n');
}
